import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Add,
  BlockOutlined,
  CheckCircleOutline,
  LayersOutlined,
  LockOutlined,
  PendingOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection, scrollTop } from "services/constants";
import { Loader } from "components/Loader";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { salaryComponentListHeader } from "pages/PayRoll/PayRollHeaders";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SystemTag, ToneChip, VersionChip } from "../Shared/salaryUi";
import {
  CATEGORIES,
  CATEGORY_TONES,
  COMPONENT_STATUS_COLORS,
  USER_CATEGORIES,
  componentToForm,
  currentVersion,
  emptyComponentForm,
  toListRow,
  versionLabel,
} from "./salaryComponentData";
import {
  activateSalaryComponent,
  checkComponentCode,
  checkComponentName,
  componentDetails,
  createSalaryComponent,
  deactivateSalaryComponent,
  deleteSalaryComponent,
  getComponentChoices,
  getPercentBases,
  listSalaryComponents,
  updateSalaryComponent,
  validateComponentValue,
} from "./salaryComponentApi";
import { SalaryComponentFormPanel, SalaryComponentViewPanel } from "./SalaryComponentPanels";

// Payroll > Salary > Salary Components, on the payroll/salary/ component
// APIs (salaryComponentApi.js).

const ALL = "all";

const STATUS_CARDS = [
  { key: ALL, title: "All", detail: "Every component in this company", icon: LayersOutlined, accent: designColors.tealDark, tint: designColors.tealTint },
  { key: "Used", title: "In use", detail: "In at least one template. Calc changes save as a new version", icon: PendingOutlined, accent: "#F0954A", tint: "#FFF4E5" },
  { key: "Unused", title: "Unused", detail: "Not in any template. Edit or delete freely", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "System", title: "System", detail: "Statutory and Pay Input lines, added at payroll", icon: LockOutlined, accent: designColors.slate600, tint: designColors.slate100 },
  { key: "Inactive", title: "Inactive", detail: "Deactivated. Can't be added to templates until reactivated", icon: BlockOutlined, accent: "#6B7280", tint: "#F3F4F6" },
];

const SYSTEM_METHODS = new Set(["Pay Input", "Statutory"]);

export const SalaryComponent = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <SalaryComponentScreen key={String(domain) + ":" + String(topbarCompanyId)} {...props} />;
};

const SalaryComponentScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Salary Component");
  const validCompany =
    Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mutationRef = useRef(null);
  // Category / method choices for the form (null = not loaded: all shown).
  const [choices, setChoices] = useState({ categories: null, methods: null });
  const [statusCard, setStatusCard] = useState(ALL);
  const [categoryTab, setCategoryTab] = useState(ALL);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewId, setViewId] = useState(null);
  const [formPanel, setFormPanel] = useState({ open: false, mode: "add", id: null, initial: null });

  const tableContainerRef = useRef(null);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  // The sticky table header sits under the CustomFilter bar, whose height
  // changes once filter chips appear (same as Users).
  useEffect(() => {
    if (!customFilterNode) return undefined;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const update = () => setFooterHeight(footerNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  useEffect(() => {
    const controller = new AbortController();
    if (!validCompany || !domain) {
      setComponents([]);
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    listSalaryComponents(domain, topbarCompanyId, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setComponents(rows); })
      // Errors show as a toast; the table stays empty.
      .catch((error) => { if (!controller.signal.aborted) { setComponents([]); showToast("error", error.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);

  useEffect(() => {
    if (!domain) return undefined;
    const controller = new AbortController();
    // Without them the form falls back to its own lists.
    getComponentChoices(domain, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setChoices(value); })
      .catch(() => {});
    return () => controller.abort();
  }, [domain]);

  useEffect(() => () => mutationRef.current?.abort(), []);

  const handleSavedViewChange = useCallback(() => setPage(0), []);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "salary_component_saved_views",
    fullHeaders: salaryComponentListHeader,
    screenAccess: canEdit,
    onViewChange: handleSavedViewChange,
  });

  // ---- Data pipeline: category tab -> status card -> CustomFilter ----
  // Same order as the Pay Inputs screens (Overtime): the tabs above the
  // cards pick the rows first, and the cards count only that tab's rows.
  // Inactive components stay listed (dimmed, status "Inactive") so they can
  // be opened and reactivated; they count under All and Inactive.

  const allRows = useMemo(() => components.map(toListRow), [components]);
  const categoryRows = useMemo(
    () => (categoryTab === ALL ? allRows : allRows.filter((row) => row.category === categoryTab)),
    [allRows, categoryTab],
  );
  const statusCounts = useMemo(
    () => ({
      [ALL]: categoryRows.length,
      Used: categoryRows.filter((row) => row.status === "Used").length,
      Unused: categoryRows.filter((row) => row.status === "Unused").length,
      System: categoryRows.filter((row) => row.status === "System").length,
      Inactive: categoryRows.filter((row) => row.status === "Inactive").length,
    }),
    [categoryRows],
  );
  const tabRows = useMemo(
    () => (statusCard === ALL ? categoryRows : categoryRows.filter((row) => row.status === statusCard)),
    [categoryRows, statusCard],
  );

  // CustomFilter reports its result one render after tabRows changes; only
  // trust it when it was computed from the current tabRows (Users pattern).
  const [filteredRows, setFilteredRows] = useState(null);
  const filteredSourceRef = useRef(null);
  const visibleRows = filteredRows !== null && filteredSourceRef.current === tabRows ? filteredRows : tabRows;
  const handleFilteredDataChange = useCallback(
    (data) => {
      filteredSourceRef.current = tabRows;
      setFilteredRows(data);
      if (page * rowsPerPage >= data.length) setPage(0);
    },
    [page, rowsPerPage, tabRows],
  );

  useEffect(() => setPage(0), [statusCard, categoryTab]);

  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  // ---- Panels ----

  // The open component's own record plus its Version History, Templates and
  // Employees tabs, loaded when it's opened and again after each change.
  const [details, setDetails] = useState({ id: null, component: null, loading: false });
  const [detailsRefresh, setDetailsRefresh] = useState(0);
  const listComponent = (id) => components.find((item) => String(item.id) === String(id)) ?? null;
  const viewListComponent = viewId == null ? null : listComponent(viewId);
  const viewListed = Boolean(viewListComponent);
  useEffect(() => {
    if (viewId == null || !viewListComponent) {
      setDetails({ id: null, component: null, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ id: viewId, component: String(current.id) === String(viewId) ? current.component : null, loading: true }));
    componentDetails(domain, viewListComponent, controller.signal)
      .then(({ component, errors }) => {
        if (controller.signal.aborted) return;
        setDetails({ id: viewId, component, loading: false });
        if (errors.length) showToast("error", errors[0]);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ id: viewId, component: null, loading: false });
        showToast("error", error.message);
      });
    return () => controller.abort();
    // The list row object changes with every list load; detailsRefresh
    // reloads after a change instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, viewId, viewListed, detailsRefresh]);
  const withDetails = (component) =>
    component && details.component && String(details.id) === String(component.id) ? { ...component, ...details.component } : component;
  const viewComponent = withDetails(viewListComponent);
  const formComponent = withDetails(formPanel.id == null ? null : listComponent(formPanel.id));

  // Percentage "Of" choices from the server, loaded as the form opens.
  const [percentBases, setPercentBases] = useState(null);
  useEffect(() => {
    if (!formPanel.open || !validCompany) return undefined;
    const controller = new AbortController();
    setPercentBases(null);
    getPercentBases(domain, topbarCompanyId, formPanel.mode === "edit" ? formPanel.id : undefined, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setPercentBases(value); })
      .catch(() => {});
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, formPanel.open, formPanel.mode, formPanel.id]);

  // Categories the form may offer: exactly what get-component-choices
  // returns (incl. Calculation), in its order; the built-in list only
  // when the server sends none.
  const formCategories = useMemo(() => {
    const server = [...new Set((choices.categories || []).filter(Boolean))];
    const list = server.length ? server : USER_CATEGORIES;
    const current = formPanel.initial?.category;
    return current && !list.includes(current) ? [current, ...list] : list;
  }, [choices.categories, formPanel.initial]);

  // View and Add/Edit share one expand state, so Edit opened from an
  // expanded view opens expanded too.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = () => setPanelExpanded((value) => !value);
  const anyPanelOpen = Boolean(viewComponent) || formPanel.open;
  useEffect(() => {
    if (!anyPanelOpen) setPanelExpanded(false);
  }, [anyPanelOpen]);

  // One save / status change at a time; errors show as a toast.
  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true;
    setBusy(true);
    const controller = new AbortController();
    mutationRef.current = controller;
    try {
      const value = await action(controller.signal);
      if (controller.signal.aborted) return { success: false };
      return { success: true, value };
    } catch (error) {
      if (!controller.signal.aborted) showToast("error", error.message);
      return { success: false };
    } finally {
      if (!controller.signal.aborted) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const reload = () => {
    setRefresh((value) => value + 1);
    setDetailsRefresh((value) => value + 1);
  };

  // Returns { fieldErrors } when the name / code checks refuse, so the form
  // shows them under the fields.
  const handleSave = async (form, { newVersion }) => {
    const adding = formPanel.mode === "add";
    const editingId = formPanel.id;
    const result = await runAction(async (signal) => {
      const [nameError, codeError] = await Promise.all([
        checkComponentName(domain, topbarCompanyId, form.name, adding ? undefined : editingId, signal),
        adding ? checkComponentCode(domain, topbarCompanyId, form.code, signal) : null,
      ]);
      if (nameError || codeError) return { fieldErrors: { name: nameError || undefined, code: codeError || undefined } };
      if (adding) return createSalaryComponent(domain, topbarCompanyId, form, signal);
      const message = await updateSalaryComponent(domain, editingId, form, { newVersion }, signal);
      return { id: editingId, message };
    });
    if (!result.success) return undefined;
    if (result.value.fieldErrors) return result.value;
    // Add goes back to the list (a new component's view isn't opened). Edit
    // is started from the view, so saving returns to that view, which
    // reloads with the saved details.
    setFormPanel((current) => ({ ...current, open: false }));
    if (adding) setViewId(null);
    reload();
    // The server's message; the local text only when it sends none.
    const fallback = adding
      ? `${form.name.trim()} created as V1.`
      : newVersion && formComponent
        ? `${form.name.trim()} saved as ${versionLabel(currentVersion(formComponent).version + 1)}.`
        : `${form.name.trim()} updated.`;
    showToast("success", result.value.message || fallback);
    return undefined;
  };

  // True when the server took it, so the panel can close its reason box.
  const handleDeactivate = async (component, reason) => {
    const result = await runAction((signal) => deactivateSalaryComponent(domain, component.id, reason, signal));
    if (!result.success) return false;
    reload();
    showToast("success", result.value || `${component.name} deactivated.`);
    return true;
  };

  const handleReactivate = async (component) => {
    const result = await runAction((signal) => activateSalaryComponent(domain, component.id, signal));
    if (!result.success) return;
    reload();
    showToast("success", result.value || `${component.name} activated.`);
  };

  // Delete (Unused components only; the server answers 400 for a used one).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDelete = async () => {
    const component = deleteTarget;
    if (!component) return;
    const result = await runAction((signal) => deleteSalaryComponent(domain, component.id, signal));
    if (!result.success) return;
    setDeleteTarget(null);
    setViewId(null);
    reload();
    showToast("success", result.value || `${component.name} deleted.`);
  };

  const handleTestFormula = useCallback(
    (values, signal) => validateComponentValue(domain, topbarCompanyId, values, signal),
    [domain, topbarCompanyId],
  );

  const openEdit = (component) =>
    setFormPanel({ open: true, mode: "edit", id: component.id, initial: componentToForm(component) });

  // ---- Table ----

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Mui.Typography noWrap sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium }}>
                {row.name}
              </Mui.Typography>
              {row.system ? <SystemTag /> : null}
              {row.balancing ? <ToneChip label="Balancing" tone="green" /> : null}
            </Mui.Stack>
            <Mui.Typography noWrap sx={designType.captionText}>
              {row.description}
            </Mui.Typography>
          </Mui.Box>
        );
      case "code":
        return row.code || "–";
      case "category":
        return <ToneChip label={row.category} tone={CATEGORY_TONES[row.category]} />;
      case "method":
        return <ToneChip label={row.method} tone="slate" dashed={SYSTEM_METHODS.has(row.method)} />;
      case "default_value":
        if (SYSTEM_METHODS.has(row.method)) {
          return <Mui.Box component="span" sx={{ fontStyle: "italic", color: designColors.slate600 }}>{row.default_value}</Mui.Box>;
        }
        return row.method === "Formula" ? (
          <Mui.Box component="span" title={row.default_value}>{row.default_value}</Mui.Box>
        ) : (
          row.default_value
        );
      case "version":
        return <VersionChip label={row.version} />;
      case "status":
        return <RequestStatusChip status={row.status} colorCode={COMPONENT_STATUS_COLORS[row.status]} />;
      default:
        return row[key] || "–";
    }
  };

  const headCellSx = { ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + customFilterHeight, whiteSpace: "nowrap" };
  // Tab counts are over every component, like the Pay Inputs source tabs.
  const categoryTabs = [
    { value: ALL, label: "All", count: allRows.length },
    ...CATEGORIES.map((category) => ({ value: category, label: category, count: allRows.filter((row) => row.category === category).length })),
  ];

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        {/* Header */}
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Salary", "Salary Components"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Salary Components
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Reusable pay lines and their default calculation. Templates copy these defaults.
            </Mui.Typography>
          </Mui.Box>
          {canEdit ? (
            <Mui.Button
              startIcon={<Add />}
              onClick={() => setFormPanel({ open: true, mode: "add", id: null, initial: emptyComponentForm() })}
              disabled={busy || loading || !validCompany}
              sx={designShell.primaryButtonBase}
            >
              Add Component
            </Mui.Button>
          ) : null}
        </Mui.Box>

        <Mui.Box sx={{ px: 2 }}>
          {/* Category tabs, above the cards (same look as the Pay Inputs
              source tabs): the cards and the table follow the selected tab. */}
          <Mui.Box sx={{ ...designShell.sectionCard, px: 2, mb: 1.5 }}>
            <Mui.Tabs
              value={categoryTab}
              onChange={(event, next) => setCategoryTab(next)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={designShell.panelTabs}
            >
              {categoryTabs.map((tab) => (
                <Mui.Tab key={tab.value} value={tab.value} label={`${tab.label} (${tab.count.toLocaleString("en-IN")})`} />
              ))}
            </Mui.Tabs>
          </Mui.Box>

          {/* Status cards (filters) for the selected tab */}
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
              gap: 1.5,
              mb: 2,
            }}
          >
            {STATUS_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={statusCounts[card.key]}
                detail={card.detail}
                accent={card.accent}
                tint={card.tint}
                onClick={() => setStatusCard(card.key)}
                isActive={statusCard === card.key}
                activeBorder
              />
            ))}
          </Mui.Box>

          {/* Toolbar */}
          <Mui.Box ref={customFilterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              title="Salary Components"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={tabRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search name or code"
            />
          </Mui.Box>

          {/* Table */}
          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer ref={tableContainerRef} sx={{ width: "100%", overflowX: "visible", pb: footerHeight ? `${footerHeight}px` : 0 }}>
              <ApproveTable stickyHeader>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    {visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} sx={headCellSx}>
                        {head.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={visibleHeaders?.length ?? 1} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={tabRows} colSpan={visibleHeaders?.length ?? 1} />
                  ) : (
                    pageRows.map((row) => (
                      <Mui.TableRow
                        key={row.id}
                        hover
                        onClick={() => setViewId(row.id)}
                        sx={{
                          cursor: "pointer",
                          ...designShell.tableRowHover,
                          ...(row.active ? {} : { "& td": { color: designColors.slate500 }, opacity: 0.75 }),
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {visibleHeaders.map((head) => (
                          <Mui.TableCell
                            key={head.key}
                            sx={{ ...sharedTableBodyCellSx, py: 1.25, maxWidth: head.key === "name" ? 320 : head.key === "default_value" ? 300 : undefined }}
                          >
                            {renderCell(row, head.key)}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>

            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "components",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <SalaryComponentViewPanel
        open={Boolean(viewComponent)}
        component={viewComponent}
        components={components}
        canEdit={canEdit}
        busy={busy}
        detailsLoading={details.loading && !details.component}
        // Only while open: GlobalSidePanel keeps an expanded panel showing
        // even after `open` turns false.
        expanded={Boolean(viewComponent) && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setViewId(null)}
        onEdit={openEdit}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onDelete={setDeleteTarget}
      />

      <SalaryComponentFormPanel
        open={formPanel.open}
        mode={formPanel.mode}
        initial={formPanel.initial}
        component={formComponent}
        components={components}
        categoryOptions={formCategories}
        methodOptions={choices.methods}
        baseOptions={percentBases}
        busy={busy}
        expanded={formPanel.open && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setFormPanel((current) => ({ ...current, open: false }))}
        onSave={handleSave}
        onTestFormula={handleTestFormula}
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete component"
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Keep"
        loading={busy}
        loadingLabel="Deleting..."
        maxWidth="sm"
        danger
      >
        Delete {deleteTarget?.name} ({deleteTarget?.code})? No template uses it. This can't be undone here.
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
